import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';

import { useCancelAppointmentMutation } from '../../app/services/appointments';
import { Modal } from '../../components';
import { toggleModal } from '../../features/ui/uiSlice';
import { AppointmentBoxListItems } from '../../components/Appointments';
import { useCheckoutContext } from '../../providers';
import { formatAppointmentForDisplay } from '../../utils/helpers';

const cancellationReasons = [
    'Cost',
    'Schedule Conflict',
    'Decided to go with another company',
    'No longer need',
    'Other',
];

function cancellationText(appointment) {
    switch (appointment.appointmentType) {
        case 'Initial Consultation':
        case 'Initial Consultation PLUS':
            return 'This visit is required to get your Home Sleep Study';
        case 'Annual Visit':
        case 'COMPLIANCE':
            return 'This visit is REQUIRED by your insurance.';
        default:
            return null;
    }
}

function confirmationText(usingInsurance) {
    if (usingInsurance) {
        return 'We have canceled your appointment. We will not submit a claim to your insurance. If you change your mind, you may place a new order at anytime.';
    } else {
        return 'We have canceled your appointment. We will process a refund to your original payment method, it should appear within the next 3-5 business days. If you change your mind, you may place a new order at anytime.';
    }
}

function MyAppointment() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentAppointment, modalsOpen, upcomingAppointment } = useSelector(
        (state) => state.ui,
    );
    const [cancelAppointment, { isLoading }] = useCancelAppointmentMutation();
    const { setCheckoutStep } = useCheckoutContext();

    const [cancellationReason, setCancellationReason] = useState('');

    const { timeframe } = formatAppointmentForDisplay(upcomingAppointment);

    return (
        <div>
            <h3 className="mb-3 text-center font-heading text-[44px] lg:text-7xl">
                {upcomingAppointment ? 'Your upcoming appointment' : 'No current appointment.'}
            </h3>
            {upcomingAppointment && (
                <>
                    <div className="mx-auto max-w-xl bg-white p-4 lg:p-16 text-gray-darker">
                        <div>
                            <p className="mb-5 text-sm font-bold text-gem-purple">
                                Session Details
                            </p>
                            <ul className="mb-8 space-y-4 text-lg">
                                <AppointmentBoxListItems appointment={upcomingAppointment} />
                            </ul>

                            <nav className="space-between flex items-center space-x-2">
                                <button
                                    className="btn-primary flex-1 h-[66px]"
                                    onClick={() => {
                                        navigate('../reschedule-appointment', {
                                            state: {
                                                from: 'appointmentRescheduling',
                                                appointmentToReschedule: upcomingAppointment,
                                            },
                                        });
                                    }}>
                                    Reschedule
                                </button>
                                <button
                                    className="btn-shell flex-1 h-[66px]"
                                    onClick={() => {
                                        dispatch(toggleModal('confirmCancellation'));
                                    }}>
                                    Cancel appointment
                                </button>
                            </nav>
                        </div>
                    </div>
                    <Modal
                        showClose={true}
                        isOpen={modalsOpen.indexOf('confirmCancellation') > -1}
                        closeHandler={() => {
                            setCancellationReason('');
                            dispatch(toggleModal('confirmCancellation'));
                        }}>
                        <div className="space-y-4">
                            <h2 className="font-heading text-5xl lg:text-7xl text-center">
                                Are you sure?
                            </h2>
                            {cancellationText(upcomingAppointment)}

                            <div className="space-y-4">
                                <p className="font-bold text-center">
                                    Select a reason for cancelling
                                </p>

                                <div className="space-y-2 flex flex-col">
                                    {cancellationReasons.map((reason) => (
                                        <button
                                            key={reason}
                                            type="button"
                                            className={`w-full text-left rounded-lg px-4 py-3 border transition
                                                ${
                                                    cancellationReason === reason
                                                        ? 'bg-gem-purple text-white border-gem-purple'
                                                        : 'bg-white text-gray-800 border-gray-300'
                                                }
                                                focus:outline-none focus:ring-2 focus:ring-gem-purple
                                            `}
                                            onClick={() => setCancellationReason(reason)}>
                                            {reason}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <button
                                className="btn-primary w-full"
                                disabled={isLoading || cancellationReason.length === 0}
                                onClick={async () => {
                                    dispatch(toggleModal('confirmCancellation'));
                                    dispatch(toggleModal('appointmentCancelledNotice'));

                                    await cancelAppointment({
                                        healthieId: upcomingAppointment.healthieId
                                            ? upcomingAppointment.healthieId
                                            : upcomingAppointment.id,
                                        isRescheduling: false,
                                        reason: cancellationReason,
                                    });
                                    setCheckoutStep(null);

                                    posthog?.capture('Cancelled appointment', {
                                        appointmentId: upcomingAppointment.id,
                                        appointmentType: upcomingAppointment?.appointmentType,
                                        providerId: upcomingAppointment.providerId,
                                        timeframe: timeframe,
                                        orderId: upcomingAppointment.order?.id,
                                        reason: cancellationReason,
                                    });
                                }}>
                                yes, cancel
                            </button>
                        </div>
                    </Modal>
                </>
            )}
            <Modal
                showClose={true}
                isOpen={modalsOpen.indexOf('appointmentCancelledNotice') > -1}
                closeHandler={() => {
                    dispatch(toggleModal('appointmentCancelledNotice'));
                }}>
                <div className="space-y-5">
                    <h2 className="font-heading text-5xl lg:text-7xl">Appointment canceled</h2>
                    <p className="text-sm lg:text-lg">
                        {confirmationText(currentAppointment?.order?.billingType === 'INSURANCE')}
                    </p>
                    <button
                        className="btn-primary"
                        onClick={() => {
                            dispatch(toggleModal('appointmentCancelledNotice'));
                            navigate('/dashboard');
                        }}>
                        Return home
                    </button>
                </div>
            </Modal>
        </div>
    );
}

export default MyAppointment;
